import { useState, useEffect } from 'react';

const THEMES = {
  classic: {
    face: '#1a1b23',
    border: '#2a2b35',
    hourHand: '#e4e4e7',
    minuteHand: '#a1a1aa',
    secondHand: '#818cf8',
    hourMarks: '#52525b',
    minuteMarks: '#2a2b35',
    center: '#818cf8',
    numbers: '#71717a',
  },
  gold: {
    face: '#1a1510',
    border: '#3d3420',
    hourHand: '#fbbf24',
    minuteHand: '#d4a017',
    secondHand: '#f59e0b',
    hourMarks: '#92702a',
    minuteMarks: '#3d3420',
    center: '#fbbf24',
    numbers: '#92702a',
  },
  minimal: {
    face: '#0f1117',
    border: '#0f1117',
    hourHand: '#e4e4e7',
    minuteHand: '#71717a',
    secondHand: '#f87171',
    hourMarks: '#2a2b35',
    minuteMarks: 'transparent',
    center: '#e4e4e7',
    numbers: 'transparent',
  },
  green: {
    face: '#0a1a0f',
    border: '#1a3a22',
    hourHand: '#34d399',
    minuteHand: '#6ee7b7',
    secondHand: '#10b981',
    hourMarks: '#1a3a22',
    minuteMarks: '#0f2518',
    center: '#34d399',
    numbers: '#1a3a22',
  },
};

function AnalogClock() {
  const [now, setNow] = useState(new Date());
  const [themeName, setThemeName] = useState(() => {
    return localStorage.getItem('meware_clock_theme') || 'classic';
  });
  const [showThemes, setShowThemes] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const theme = THEMES[themeName] || THEMES.classic;

  // Use CDT time for clock hands
  const cdtStr = now.toLocaleString('en-US', { timeZone: 'America/Chicago' });
  const cdt = new Date(cdtStr);
  const hours = cdt.getHours() % 12;
  const minutes = cdt.getMinutes();
  const seconds = cdt.getSeconds();

  const hourAngle = (hours + minutes / 60) * 30;
  const minuteAngle = (minutes + seconds / 60) * 6;
  const secondAngle = seconds * 6;

  function selectTheme(name) {
    setThemeName(name);
    localStorage.setItem('meware_clock_theme', name);
    setShowThemes(false);
  }

  // Generate hour marks and numbers
  const marks = [];
  for (let i = 0; i < 60; i++) {
    const angle = i * 6;
    const isHour = i % 5 === 0;
    const r1 = isHour ? 85 : 90;
    const r2 = 95;
    const rad = (angle - 90) * (Math.PI / 180);
    marks.push({
      x1: 100 + r1 * Math.cos(rad),
      y1: 100 + r1 * Math.sin(rad),
      x2: 100 + r2 * Math.cos(rad),
      y2: 100 + r2 * Math.sin(rad),
      isHour,
    });
  }

  const hourNumbers = [];
  for (let i = 1; i <= 12; i++) {
    const angle = i * 30;
    const rad = (angle - 90) * (Math.PI / 180);
    hourNumbers.push({
      num: i,
      x: 100 + 74 * Math.cos(rad),
      y: 100 + 74 * Math.sin(rad),
    });
  }

  return (
    <section className="card analog-clock-section">
      <div className="analog-clock-wrapper" onClick={() => setShowThemes(!showThemes)}>
        <svg viewBox="0 0 200 200" className="analog-clock-svg">
          {/* Face */}
          <circle cx="100" cy="100" r="98" fill={theme.face} stroke={theme.border} strokeWidth="2" />

          {/* Minute marks */}
          {marks.map((m, i) => (
            <line
              key={i}
              x1={m.x1} y1={m.y1} x2={m.x2} y2={m.y2}
              stroke={m.isHour ? theme.hourMarks : theme.minuteMarks}
              strokeWidth={m.isHour ? 2.5 : 1}
              strokeLinecap="round"
            />
          ))}

          {/* Hour numbers */}
          {hourNumbers.map((h) => (
            <text
              key={h.num}
              x={h.x} y={h.y}
              textAnchor="middle"
              dominantBaseline="central"
              fill={theme.numbers}
              fontSize="14"
              fontWeight="600"
            >
              {h.num}
            </text>
          ))}

          {/* Hour hand */}
          <line
            x1="100" y1="100" x2="100" y2="45"
            stroke={theme.hourHand} strokeWidth="4" strokeLinecap="round"
            transform={`rotate(${hourAngle} 100 100)`}
          />

          {/* Minute hand */}
          <line
            x1="100" y1="100" x2="100" y2="28"
            stroke={theme.minuteHand} strokeWidth="3" strokeLinecap="round"
            transform={`rotate(${minuteAngle} 100 100)`}
          />

          {/* Second hand */}
          <line
            x1="100" y1="115" x2="100" y2="22"
            stroke={theme.secondHand} strokeWidth="1.5" strokeLinecap="round"
            transform={`rotate(${secondAngle} 100 100)`}
          />

          {/* Center dot */}
          <circle cx="100" cy="100" r="4" fill={theme.center} />
        </svg>
      </div>
      {showThemes && (
        <div className="clock-themes">
          {Object.keys(THEMES).map((name) => (
            <button
              key={name}
              className={`clock-theme-btn${themeName === name ? ' active' : ''}`}
              onClick={(e) => { e.stopPropagation(); selectTheme(name); }}
            >
              <span
                className="clock-theme-dot"
                style={{ background: THEMES[name].secondHand }}
              />
              {name}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

export default AnalogClock;
